/**
 * Bi-directional sync (Build → design). When the user changes the game through
 * Build, this dialog runs an AI pass that proposes edits to the design sections
 * so the spec stays true to the game, and lets the user review each one as a
 * red/green diff and accept/reject per section before anything is written.
 *
 * The reverse of "Send Build Updates": that pushes design → game; this pulls
 * game (Build) → design. See `design.ts` reconcileFromChat / applyReconcile.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { DesignReconcileProposal } from '@volley/crucible-ipc-types';
import { DesignModal } from './DesignModal';
import { applyReconcile, reconcileFromChat } from './anvil-api';
import { diffLines, type DiffRowType } from './lib/line-diff';
import {
  itemModeFor,
  mergeItemsMarkdown,
  parseItems,
  isGameLoopSection,
} from './lib/section-items';

const DIFF_ROW_CLASS: Record<DiffRowType, string> = {
  add: 'bg-green-500/15 text-green-300',
  del: 'bg-red-500/15 text-red-300',
  context: 'text-crucible-muted',
};
const DIFF_ROW_SIGN: Record<DiffRowType, string> = { add: '+', del: '-', context: ' ' };

export interface ReconcileMutationResult {
  applied: boolean;
  filenames: string[];
}

export interface ReconcileDialogProps {
  gameId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after sections are written or the prompt is dismissed. */
  onSettled?: (result: ReconcileMutationResult) => void;
  /** Called after the apply/dismiss request completes, even if this run is stale. */
  onMutationComplete?: (result: ReconcileMutationResult) => void;
  /** Whether a section uses the item editor (defaults to the canonical-id rule).
   *  For item sections the chat-driven update is MERGED into the existing items
   *  by name rather than replacing the body, so nothing is dropped. */
  isItemSection?: (sectionId: string) => boolean;
}

/** For an item section, merge the AI's chat-driven body into the original by
 *  name (preserving items it didn't touch) instead of replacing wholesale.
 *  Falls back to the AI body when the section isn't item-mode or the AI didn't
 *  return parseable items (so a prose update still applies). */
function reconcileBody(
  proposal: DesignReconcileProposal,
  isItemSection: (sectionId: string) => boolean,
): string {
  if (!isItemSection(proposal.sectionId)) return proposal.newBody;
  if (parseItems(proposal.newBody).items.length === 0) return proposal.newBody;
  return mergeItemsMarkdown(proposal.originalBody, proposal.newBody, {
    pinGameLoop: isGameLoopSection(proposal.sectionId),
  });
}

type Stage = 'working' | 'review' | 'error';

interface Reviewable extends DesignReconcileProposal {
  accepted: boolean;
}

export function ReconcileDialog({
  gameId,
  open,
  onOpenChange,
  onSettled,
  onMutationComplete,
  isItemSection = (id) => itemModeFor({ id }),
}: ReconcileDialogProps): ReactElement {
  const [stage, setStage] = useState<Stage>('working');
  const [proposals, setProposals] = useState<Reviewable[]>([]);
  const [chatCount, setChatCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const runRef = useRef(0);

  useEffect(() => {
    if (!open) {
      runRef.current += 1;
      return;
    }
    const myRun = (runRef.current += 1);
    const live = (): boolean => runRef.current === myRun;
    setStage('working');
    setProposals([]);
    setError(null);
    reconcileFromChat(gameId)
      .then((res) => {
        if (!live()) return;
        setChatCount(res.chatCount);
        // For item sections, merge the chat-driven update into the existing
        // items by name so weapons etc. land as cards and nothing is dropped.
        setProposals(
          res.proposals.map((p) => ({
            ...p,
            newBody: reconcileBody(p, isItemSection),
            accepted: true,
          })),
        );
        setStage('review');
      })
      .catch((e: unknown) => {
        if (!live()) return;
        setError(e instanceof Error ? e.message : String(e));
        setStage('error');
      });
  }, [open, gameId]);

  /**
   * Applies accepted reverse-reconcile proposals and reports written filenames.
   *
   * Args:
   *   None.
   *
   * Returns:
   *   Promise that settles after the write and parent callbacks complete.
   *
   * Raises:
   *   None. Failures are rendered in-dialog.
   */
  const apply = useCallback(async () => {
    const accepted = proposals.filter((p) => p.accepted);
    // Pass `original` so the main process can abort if the section drifted since
    // this review started (TOCTOU guard) rather than overwrite a fresh edit.
    const sections = accepted.map((p) => ({
      filename: p.filename,
      body: p.newBody,
      original: p.originalBody,
    }));
    const myRun = runRef.current;
    const live = (): boolean => runRef.current === myRun;
    try {
      await applyReconcile(gameId, sections, chatCount);
      const result = {
        applied: sections.length > 0,
        filenames: sections.map((s) => s.filename),
      };
      onMutationComplete?.(result);
      if (!live()) return;
      onSettled?.(result);
      onOpenChange(false);
    } catch (e: unknown) {
      if (!live()) return;
      setError(e instanceof Error ? e.message : String(e));
      setStage('error');
    }
  }, [proposals, chatCount, gameId, onMutationComplete, onSettled, onOpenChange]);

  /**
   * Marks recent Build activity reviewed without writing design sections.
   *
   * Args:
   *   None.
   *
   * Returns:
   *   Promise that settles after the marker advancement attempt completes.
   *
   * Raises:
   *   None. Marker write failures are best-effort so the prompt can reappear.
   */
  const dismiss = useCallback(async () => {
    const myRun = runRef.current;
    const live = (): boolean => runRef.current === myRun;
    try {
      // Dismiss advances the marker only; if it fails, the same turns re-propose later.
      await applyReconcile(gameId, [], chatCount);
    } catch {
      // Best-effort: keep dismissal responsive even when marker persistence fails.
    }
    const result = { applied: false, filenames: [] };
    onMutationComplete?.(result);
    if (!live()) return;
    onSettled?.(result);
    onOpenChange(false);
  }, [gameId, chatCount, onMutationComplete, onSettled, onOpenChange]);

  return (
    <DesignModal open={open} onOpenChange={onOpenChange} title="Review Build changes">
      {stage === 'working' && (
        <div
          className="py-6 text-center text-sm text-crucible-muted"
          data-testid="reconcile-working"
        >
          Reviewing recent Build activity...
        </div>
      )}

      {stage === 'error' && (
        <div data-testid="reconcile-error">
          <p className="mb-3 rounded border border-red-500/40 p-2 text-xs text-red-400">{error}</p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded border border-crucible-border px-3 py-1.5 text-sm text-crucible-muted hover:bg-crucible-bg-elevated"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {stage === 'review' && (
        <div data-testid="reconcile-review">
          {proposals.length === 0 ? (
            <>
              <p className="text-sm font-medium text-crucible-fg" data-testid="reconcile-none">
                No design-significant changes found.
              </p>
              <p className="mb-4 mt-1 text-sm text-crucible-muted">
                Recent Build activity did not require updates to the Design docs.
              </p>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => void dismiss()}
                  className="rounded bg-crucible-accent px-3 py-1.5 text-sm font-medium text-white"
                >
                  Got it
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="mb-3 text-xs text-crucible-muted">
                {proposals.filter((p) => p.accepted).length} of {proposals.length} section
                {proposals.length === 1 ? '' : 's'} selected to update from recent Build activity.
              </p>
              <ul className="space-y-4">
                {proposals.map((p) => (
                  <li key={p.sectionId} className="rounded border border-crucible-border p-2">
                    <label className="flex items-center gap-2 text-sm text-crucible-fg">
                      <input
                        type="checkbox"
                        checked={p.accepted}
                        onChange={(e) =>
                          setProposals((prev) =>
                            prev.map((x) =>
                              x.sectionId === p.sectionId
                                ? { ...x, accepted: e.target.checked }
                                : x,
                            ),
                          )
                        }
                      />
                      <span className="font-medium">{p.title}</span>
                      {p.rationale && <span className="text-crucible-muted">— {p.rationale}</span>}
                    </label>
                    <div className="mt-2 rounded bg-crucible-bg-elevated p-2">
                      <div className="max-h-60 overflow-y-auto rounded font-mono text-[11px] leading-snug">
                        {diffLines(p.originalBody, p.newBody).map((row, idx) => (
                          <div
                            key={idx}
                            className={`flex gap-1.5 px-1 ${DIFF_ROW_CLASS[row.type]}`}
                          >
                            <span aria-hidden className="select-none opacity-50">
                              {DIFF_ROW_SIGN[row.type]}
                            </span>
                            <span className="min-w-0 whitespace-pre-wrap break-words">
                              {row.text || ' '}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => void dismiss()}
                  className="rounded border border-crucible-border px-3 py-1.5 text-sm text-crucible-muted hover:bg-crucible-bg-elevated"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={() => void apply()}
                  disabled={!proposals.some((p) => p.accepted)}
                  className="rounded bg-crucible-accent px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
                >
                  Update design
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </DesignModal>
  );
}
